import React, { Component } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Collapse,
  List,
  Space,
  message,
  Popconfirm,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import TagInput from './widgets/TagInput';

const { TextArea } = Input;
const { Panel } = Collapse;

class SalesStagesTab extends Component {
  state = {
    editModalVisible: false,
    editingStage: null,
    localStages: [],
  };

  // Default stages
  defaultStages = [
    {
      id: 'greeting',
      name: 'GREETING',
      display_name: 'Chào hỏi',
      goal: 'Chào hỏi, tạo thiện cảm ban đầu với khách hàng',
      color: '#3B82F6',
      sequence_order: 1,
    },
    {
      id: 'discovery',
      name: 'DISCOVERY',
      display_name: 'Khám phá',
      goal: 'Tìm hiểu nhu cầu, thu thập thông tin khách hàng',
      color: '#8B5CF6',
      sequence_order: 2,
    },
    {
      id: 'negotiation',
      name: 'NEGOTIATION',
      display_name: 'Tư vấn',
      goal: 'Tư vấn sản phẩm, xử lý objection, thương lượng',
      color: '#F59E0B',
      sequence_order: 3,
    },
    {
      id: 'closing',
      name: 'CLOSING',
      display_name: 'Chốt đơn',
      goal: 'Chốt đơn, thu thập địa chỉ/SĐT giao hàng',
      color: '#10B981',
      sequence_order: 4,
    },
    {
      id: 'handover',
      name: 'HANDOVER',
      display_name: 'Chuyển NV',
      goal: 'Chuyển cuộc trò chuyện cho nhân viên thật',
      color: '#EF4444',
      sequence_order: 5,
    },
  ];

  constructor(props) {
    super(props);
    // Initialize with default stages if no stages provided
    this.state.localStages = props.stages && props.stages.length > 0
      ? props.stages
      : this.defaultStages;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.stages !== this.props.stages) {
      this.setState({
        localStages: this.props.stages && this.props.stages.length > 0
          ? this.props.stages
          : this.defaultStages,
      });
    }
  }

  // Move stage up or down
  moveStage = (index, direction) => {
    const stages = [...this.state.localStages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= stages.length) return;

    // Swap
    [stages[index], stages[newIndex]] = [stages[newIndex], stages[index]];

    // Update sequence order
    stages.forEach((stage, idx) => {
      stage.sequence_order = idx + 1;
    });

    this.setState({ localStages: stages });
    this.notifyChange(stages);
  };

  openEditModal = (stage) => {
    this.setState({
      editModalVisible: true,
      editingStage: { ...stage },
    });
  };

  closeEditModal = () => {
    this.setState({
      editModalVisible: false,
      editingStage: null,
    });
  };

  saveStage = () => {
    const { editingStage } = this.state;
    const stages = this.state.localStages.map((s) =>
      s.id === editingStage.id ? editingStage : s
    );

    this.setState({ localStages: stages, editModalVisible: false, editingStage: null });
    this.notifyChange(stages);
    message.success('Stage đã được cập nhật');
  };

  deleteStage = (stageId) => {
    // Prevent deleting default stages - just deactivate
    const stages = this.state.localStages.filter((s) => s.id !== stageId);
    this.setState({ localStages: stages });
    this.notifyChange(stages);
    message.success('Stage đã được xóa');
  };

  notifyChange = (stages) => {
    if (this.props.onChange) {
      this.props.onChange(stages);
    }
  };

  renderStageCard = (stage, index) => {
    const { localStages } = this.state;
    const isFirst = index === 0;
    const isLast = index === localStages.length - 1;

    return (
      <Card
        key={stage.id}
        size="small"
        style={{
          marginBottom: 12,
          borderLeft: `4px solid ${stage.color || '#3B82F6'}`,
        }}
        actions={[
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => this.openEditModal(stage)}
          >
            Sửa
          </Button>,
          <Popconfirm
            title="Xóa stage này?"
            onConfirm={() => this.deleteStage(stage.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>,
        ]}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Drag handle indicator */}
            <div style={{ color: '#ccc', cursor: 'grab' }}>
              <DragOutlined />
            </div>

            {/* Stage number */}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: stage.color || '#3B82F6',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: 14,
              }}
            >
              {index + 1}
            </div>

            {/* Stage info */}
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                {stage.display_name || stage.name}
              </div>
              <div style={{ color: '#666', fontSize: 13 }}>
                {stage.goal}
              </div>
            </div>
          </div>

          {/* Movement buttons */}
          <Space>
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={() => this.moveStage(index, 'up')}
              disabled={isFirst}
              size="small"
              title="Di chuyển lên"
            />
            <Button
              type="text"
              icon={<ArrowDownOutlined />}
              onClick={() => this.moveStage(index, 'down')}
              disabled={isLast}
              size="small"
              title="Di chuyển xuống"
            />
          </Space>
        </div>

        {/* Transition info */}
        {stage.transition_rules && stage.transition_rules.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
              Chuyển tiếp sang:
            </div>
            <Space wrap>
              {stage.transition_rules.map((rule, idx) => (
                <Tag key={idx} color={rule.next_stage_color || 'blue'}>
                  {rule.next_stage}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>
    );
  };

  render() {
    const { onSave, loading } = this.props;
    const { localStages, editModalVisible, editingStage } = this.state;

    return (
      <div className="sales-stages-tab">
        <Card
          title="Các giai đoạn bán hàng"
          extra={
            <Space>
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  const newStage = {
                    id: `stage_${Date.now()}`,
                    name: `STAGE_${localStages.length + 1}`,
                    display_name: 'Stage mới',
                    goal: 'Mô tả mục tiêu',
                    color: '#3B82F6',
                    sequence_order: localStages.length + 1,
                    transition_rules: [],
                  };
                  this.openEditModal(newStage);
                }}
              >
                Thêm Stage
              </Button>
              <Button type="primary" onClick={onSave} loading={loading}>
                Lưu thay đổi
              </Button>
            </Space>
          }
        >
          {/* Funnel Visual */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
            padding: 16,
            background: '#fafafa',
            borderRadius: 8,
          }}>
            {localStages.map((stage, idx) => (
              <React.Fragment key={stage.id}>
                <div
                  style={{
                    padding: '8px 16px',
                    background: stage.color || '#3B82F6',
                    color: '#fff',
                    borderRadius: 4,
                    fontWeight: 500,
                    fontSize: 12,
                  }}
                >
                  {idx + 1}. {stage.display_name || stage.name}
                </div>
                {idx < localStages.length - 1 && (
                  <div style={{ margin: '0 8px', color: '#ccc' }}>→</div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Stage List */}
          <List
            dataSource={localStages}
            renderItem={(stage, index) => this.renderStageCard(stage, index)}
            locale={{
              emptyText: <Empty description="Chưa có stage nào" />,
            }}
          />

          {/* Edit Modal */}
          <Modal
            title={editingStage ? `Chỉnh sửa: ${editingStage.display_name || editingStage.name}` : 'Tạo Stage mới'}
            open={editModalVisible}
            onOk={this.saveStage}
            onCancel={this.closeEditModal}
            width={600}
            okText="Lưu"
            cancelText="Hủy"
          >
            {editingStage && (
              <Form layout="vertical">
                <Form.Item label="Tên hiển thị">
                  <Input
                    value={editingStage.display_name}
                    onChange={(e) =>
                      this.setState({
                        editingStage: { ...editingStage, display_name: e.target.value },
                      })
                    }
                    placeholder="VD: Chào hỏi"
                  />
                </Form.Item>

                <Form.Item label="Mục tiêu">
                  <TextArea
                    rows={2}
                    value={editingStage.goal}
                    onChange={(e) =>
                      this.setState({
                        editingStage: { ...editingStage, goal: e.target.value },
                      })
                    }
                    placeholder="Mô tả mục tiêu của stage này"
                  />
                </Form.Item>

                <Form.Item label="Màu sắc">
                  <Input
                    type="color"
                    value={editingStage.color || '#3B82F6'}
                    onChange={(e) =>
                      this.setState({
                        editingStage: { ...editingStage, color: e.target.value },
                      })
                    }
                    style={{ width: 100, padding: 4 }}
                  />
                </Form.Item>

                <Form.Item label="Prompt riêng cho stage">
                  <TextArea
                    rows={4}
                    value={editingStage.stage_prompt || ''}
                    onChange={(e) =>
                      this.setState({
                        editingStage: { ...editingStage, stage_prompt: e.target.value },
                      })
                    }
                    placeholder="Prompt bổ sung cho stage này (nếu có)"
                  />
                </Form.Item>

                <Form.Item label="Điều kiện chuyển tiếp">
                  <Collapse>
                    {(editingStage.transition_rules || []).map((rule, idx) => (
                      <Panel
                        key={idx}
                        header={`Quy tắc ${idx + 1}: Chuyển sang ${rule.next_stage || '?'}`}
                      >
                        <Form.Item label="Keywords kích hoạt">
                          <TagInput
                            tags={rule.keywords || []}
                            onChange={(tags) => {
                              const rules = [...(editingStage.transition_rules || [])];
                              rules[idx] = { ...rule, keywords: tags };
                              this.setState({
                                editingStage: { ...editingStage, transition_rules: rules },
                              });
                            }}
                            placeholder="Nhập keyword"
                          />
                        </Form.Item>
                        <Form.Item label="Stage tiếp theo">
                          <Input
                            value={rule.next_stage}
                            onChange={(e) => {
                              const rules = [...(editingStage.transition_rules || [])];
                              rules[idx] = { ...rule, next_stage: e.target.value };
                              this.setState({
                                editingStage: { ...editingStage, transition_rules: rules },
                              });
                            }}
                            placeholder="VD: NEGOTIATION"
                          />
                        </Form.Item>
                      </Panel>
                    ))}
                  </Collapse>
                  <Button
                    type="dashed"
                    block
                    style={{ marginTop: 8 }}
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const rules = [
                        ...(editingStage.transition_rules || []),
                        { keywords: [], next_stage: '', conditions: [] },
                      ];
                      this.setState({
                        editingStage: { ...editingStage, transition_rules: rules },
                      });
                    }}
                  >
                    Thêm quy tắc chuyển tiếp
                  </Button>
                </Form.Item>
              </Form>
            )}
          </Modal>
        </Card>
      </div>
    );
  }
}

export default SalesStagesTab;
